import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Download, Eye, FileSpreadsheet, FileText, Inbox, Loader2, MoreVertical, Search, ShieldAlert, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import EmptyState from 'components/ui/EmptyState';
import { ColumnsToggle, TableSearch } from 'components/ui/data-table';
import { DateRangeFilter } from 'components/ui/date-range-filter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { FilterReset } from 'components/ui/filter-button';
import { PageHeader } from 'components/ui/page-header';
import { CompactPager, TablePager } from 'components/ui/pagination';
import Skeleton from 'components/ui/skeleton';
import { StatusBadge, scoreTextClass } from 'components/ui/status-badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useToast } from 'components/ui/use-toast';
import { useIsMobile } from 'hooks/use-media-query';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { formatDayTime } from 'lib/dateFormat';
import { formatClockDuration } from 'lib/timeFormat';
import type { DateRange } from 'lib/dateRange';
import { cn } from 'lib/utils';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import * as ResultModule from 'modules/result';
import { http } from 'services';

type Result = ResultModule.Types.IEntity.Result;
type IdentityParams = Pick<ResultModule.Types.IApi.List.Params, 'firstName' | 'lastName' | 'phone'>;
type Translate = (key: string, opts?: Record<string, unknown>) => string;

const PHONE_LIKE = /^[+\d\s()-]+$/;

/**
 * One box for "name or phone": digits search the phone; words search the name
 * in any order (the API matches each name term against the full name).
 */
export const identityParams = (query: string): IdentityParams => {
  const q = query.trim();
  if (!q) return {};
  if (PHONE_LIKE.test(q) && /\d/.test(q)) return { phone: q.replace(/\D/g, '') };
  const [first, ...rest] = q.split(/\s+/);
  return { firstName: first, lastName: rest.join(' ') || undefined };
};


function ResultBadge({ row, t }: { row: Result; t: Translate }) {
  if (row.status === 'in_progress') return <StatusBadge tone="warning">{t('in_progress')}</StatusBadge>;
  if (row.status === 'abandoned') return <StatusBadge tone="neutral">{t('abandoned')}</StatusBadge>;
  if (row.passed == null) return <span className="text-muted-foreground/60">—</span>;
  return row.passed ? (
    <StatusBadge tone="success">{t('passed')}</StatusBadge>
  ) : (
    <StatusBadge tone="error">{t('not_passed')}</StatusBadge>
  );
}

/** "82%" in its band colour; an attempt still running has no score yet. */
function ScorePercent({ row, className }: { row: Result; className?: string }) {
  if (row.status === 'in_progress') return <span className="text-muted-foreground/60">—</span>;
  return (
    <span className={cn('font-semibold tabular-nums', scoreTextClass(row.percentage), className)}>
      {Math.round(row.percentage)}%
    </span>
  );
}

const LoadingRows: React.FC = () => (
  <div className="divide-y divide-border" aria-hidden>
    {[0, 1, 2, 3, 4, 5].map((row) => (
      <div key={row} className="flex items-center gap-6 px-4 py-3.5">
        <Skeleton className={cn('h-3.5', row % 2 ? 'w-40' : 'w-52')} />
        <span className="flex-1" />
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="hidden h-3.5 w-24 md:block" />
      </div>
    ))}
  </div>
);

export const ResultsTablePage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const canViewSession = usePermission(PERMISSIONS.RESULTS_VIEW);

  const { input: searchInput, setInput: setSearchInput, query: search } = useSearchParamState();
  const [endDate, setEndDate] = useState<DateRange>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // What the delete confirmation will remove: the selection, or one row from its menu.
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cols, setCols] = useState({
    phone: true,
    email: true,
    subjects: true,
    score: true,
    percentage: true,
    result: true,
    proctoringEvents: true,
    endDate: true,
    time: true,
  });
  const { page, pageSize: perPage, setPage, setPageSize: setPerPage } = usePaginationParams({
    defaultPage: 1,
    defaultPageSize: 20,
  });

  // A new search or date range returns to page 1 — but not on first render,
  // so a deep-linked ?page= survives the initial load.
  const filterKey = `${search}|${endDate.from ?? ''}|${endDate.to ?? ''}`;
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setPage(1);
  }, [filterKey, setPage]);

  // Selection is scoped to the visible page, so a bulk delete never reaches rows the user can't see.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterKey, page, perPage]);

  const { items, meta, subjects, isLoading, isError, isPlaceholderData, refetch } = ResultModule.Hooks.useList({
    keepPrevious: true,
    params: {
      testId: testId || undefined,
      ...identityParams(search),
      endDateFrom: endDate.from ? `${endDate.from}T00:00:00` : undefined,
      endDateTo: endDate.to ? `${endDate.to}T23:59:59` : undefined,
      page,
      perPage,
    },
  });
  const resolvedSubjects = subjects || meta.subjects || [];
  const totalPages = meta.totalPages || 0;

  // Deleting the last rows of the last page would otherwise strand the user on an empty page.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [totalPages, page, setPage]);

  const hasFilters = Boolean(searchInput.trim() || endDate.from || endDate.to);
  const isFiltering = Boolean(search || endDate.from || endDate.to);

  const clearFilters = () => {
    setSearchInput('');
    setEndDate({});
  };

  const allSelected = items.length > 0 && items.every((r) => selectedIds.has(r.id));
  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(items.map((r) => r.id)) : new Set());
  };
  const handleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!deleteIds?.length) return;
    setIsDeleting(true);
    try {
      const { data } = await ResultModule.Api.BulkDelete(deleteIds);
      toast.success(t('deleted'), {
        description: t('results_deleted_count', { count: (data as { deleted?: number } | undefined)?.deleted ?? 0 }),
      });
      setSelectedIds((prev) => new Set(Array.from(prev).filter((id) => !deleteIds.includes(id))));
      refetch();
    } catch (e: unknown) {
      const message =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(t('error'), { description: message || t('failed_to_delete') });
    } finally {
      setIsDeleting(false);
      setDeleteIds(null);
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: 'xlsx' | 'csv' = 'xlsx') => {
    if (!testId || isExporting) return;
    setIsExporting(true);
    try {
      const response = await http.request.get(`/api/exports/results/${testId}?format=${format}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `results-${testId}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('export_started'), { description: t('results_export_downloaded') });
    } catch {
      toast.error(t('error'), { description: t('export_failed') });
    } finally {
      setIsExporting(false);
    }
  };

  const detailsPath = (row: Result) => `/app/tests/${testId}/sheets-review?attemptId=${row.id}`;
  const selecting = selectedIds.size > 0;

  const renderRows = () => {
    if (isMobile) {
      return (
        <ul className="divide-y divide-border">
          {items.map((row) => {
            const proctoringCount = row.proctoringEventsCount ?? row.tabSwitches ?? 0;
            const body = (
              <>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-[15px] font-medium text-foreground">{row.userName || '—'}</p>
                    {proctoringCount > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400"
                        title={`${proctoringCount} ${t('proctoring_events')}`}
                      >
                        <ShieldAlert className="h-3 w-3" aria-hidden />
                        {proctoringCount}
                      </span>
                    )}
                  </div>
                  {row.phone && (
                    <p className="truncate text-[12px] tabular-nums text-muted-foreground">{row.phone}</p>
                  )}
                  <p className="mt-0.5 text-[12.5px] tabular-nums text-muted-foreground">
                    {formatDayTime(row.completedAt)} · {formatClockDuration(row.duration)}
                  </p>
                </div>
                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                  <ScorePercent row={row} className="text-[15px] font-bold" />
                  <ResultBadge row={row} t={t} />
                </div>
              </>
            );
            return (
              <li key={row.id}>
                {canViewSession ? (
                  <Link to={detailsPath(row)} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40">
                    {body}
                  </Link>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      );
    }

    return (
      <div className="relative">
        {/* Selecting rows swaps the column header for the bulk actions (same height, so nothing shifts). */}
        {selecting && (
          <div
            role="toolbar"
            aria-label={t('n_of_m_selected', { selected: selectedIds.size, total: items.length })}
            className="absolute inset-x-0 top-0 z-10 flex h-10 items-center gap-3 bg-foreground pl-4 pr-2 text-[13px] text-background"
          >
            <Checkbox
              checked={allSelected ? true : 'indeterminate'}
              onCheckedChange={(v) => handleSelectAll(v === true)}
              aria-label={t('select_all')}
              className="border-background/50"
            />
            <span className="font-semibold tabular-nums">
              {t('n_of_m_selected', { selected: selectedIds.size, total: items.length })}
            </span>
            <span aria-hidden className="h-[18px] w-px bg-background/25" />
            <button
              type="button"
              onClick={() => setDeleteIds(Array.from(selectedIds))}
              disabled={isDeleting}
              className="inline-flex h-[30px] items-center gap-1.5 rounded-md px-2.5 font-medium text-red-300 transition-colors hover:bg-background/10 disabled:opacity-50 dark:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              {t('delete')}
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto inline-flex h-[30px] items-center rounded-md px-2.5 text-background/70 transition-colors hover:bg-background/10 hover:text-background"
            >
              {t('clear_selection')}
            </button>
          </div>
        )}
        <Table className="whitespace-nowrap">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10 pr-0">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(value) => handleSelectAll(value === true)}
                  aria-label={t('select_all')}
                  tabIndex={selecting ? -1 : undefined}
                />
              </TableHead>
              <TableHead className="min-w-[170px]">{t('respondent')}</TableHead>
              {cols.phone && <TableHead className="min-w-[130px]">{t('phone')}</TableHead>}
              {cols.email && <TableHead className="min-w-[160px]">{t('email')}</TableHead>}
              {cols.subjects &&
                resolvedSubjects.map((subj) => (
                  <TableHead key={subj.id} className="min-w-[120px]">
                    <div className="flex flex-col">
                      <span className="truncate">{subj.name}</span>
                      {subj.maxScore > 0 && (
                        <span className="text-[11px] font-normal text-muted-foreground">
                          ({t('max_points_short')} {subj.maxScore})
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
              {cols.score && <TableHead className="min-w-[120px]">{t('overall_score')}</TableHead>}
              {cols.percentage && <TableHead className="min-w-[90px]">{t('percentage')}</TableHead>}
              {cols.result && <TableHead className="min-w-[110px]">{t('result')}</TableHead>}
              {cols.proctoringEvents && <TableHead className="min-w-[130px]">{t('proctoring_events')}</TableHead>}
              {cols.endDate && <TableHead className="hidden min-w-[140px] lg:table-cell">{t('end_date')}</TableHead>}
              {cols.time && <TableHead className="min-w-[90px]">{t('time')}</TableHead>}
              <TableHead className="w-12">
                <span className="sr-only">{t('more_options')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((row) => {
              const isSelected = selectedIds.has(row.id);
              return (
                <TableRow key={row.id} className={cn('h-14', isSelected && 'bg-muted/60 hover:bg-muted/60')}>
                  <TableCell className="pr-0">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(value) => handleSelectOne(row.id, value === true)}
                      aria-label={t('select')}
                    />
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    {canViewSession ? (
                      <Link to={detailsPath(row)} className="block truncate text-sm font-medium text-foreground hover:underline">
                        {row.userName || '—'}
                      </Link>
                    ) : (
                      <span className="block truncate text-sm font-medium text-foreground">{row.userName || '—'}</span>
                    )}
                  </TableCell>
                  {cols.phone && (
                    <TableCell className="whitespace-nowrap text-[13px] tabular-nums text-muted-foreground">
                      {row.phone || '—'}
                    </TableCell>
                  )}
                  {cols.email && (
                    <TableCell className="max-w-[180px] truncate text-[13px] text-muted-foreground" title={row.email}>
                      {row.email || '—'}
                    </TableCell>
                  )}
                  {cols.subjects &&
                    resolvedSubjects.map((subj) => {
                      const sScore = row.subjectScores?.[subj.id];
                      return (
                        <TableCell key={subj.id} className="whitespace-nowrap text-[13px] tabular-nums">
                          {row.status === 'in_progress' ? (
                            <span className="text-muted-foreground/60">—</span>
                          ) : sScore != null ? (
                            <span className="font-semibold text-foreground">
                              {sScore}{' '}
                              {subj.maxScore > 0 && (
                                <span className="font-normal text-[12px] text-muted-foreground">/ {subj.maxScore}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                      );
                    })}
                  {cols.score && (
                    <TableCell className="whitespace-nowrap">
                      {row.status === 'in_progress' ? (
                        <span className="text-muted-foreground/60">—</span>
                      ) : (
                        <span className="text-[13px] font-semibold tabular-nums text-foreground">
                          {row.score}{' '}
                          <span className="font-normal text-[12px] text-muted-foreground">/ {row.maxScore}</span>
                        </span>
                      )}
                    </TableCell>
                  )}
                  {cols.percentage && (
                    <TableCell className="whitespace-nowrap">
                      <ScorePercent row={row} />
                    </TableCell>
                  )}
                  {cols.result && (
                    <TableCell className="whitespace-nowrap">
                      <ResultBadge row={row} t={t} />
                    </TableCell>
                  )}
                  {cols.proctoringEvents && (
                    <TableCell className="whitespace-nowrap text-[13px] tabular-nums">
                      {(row.proctoringEventsCount ?? row.tabSwitches ?? 0) > 0 ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400"
                          title={`${row.proctoringEventsCount ?? row.tabSwitches} ${t('proctoring_events')}`}
                        >
                          <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
                          {row.proctoringEventsCount ?? row.tabSwitches}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/60">0</span>
                      )}
                    </TableCell>
                  )}
                  {cols.endDate && (
                    <TableCell className="hidden whitespace-nowrap text-[13px] tabular-nums text-muted-foreground lg:table-cell">
                      {formatDayTime(row.completedAt)}
                    </TableCell>
                  )}
                  {cols.time && <TableCell className="whitespace-nowrap text-[13px] tabular-nums">{formatClockDuration(row.duration)}</TableCell>}
                  <TableCell className="py-0 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label={t('more_options')}
                        >
                          <MoreVertical className="h-4 w-4" aria-hidden />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {canViewSession && (
                          <>
                            <DropdownMenuItem asChild className="gap-2">
                              <Link to={detailsPath(row)}>
                                <Eye size={14} aria-hidden />
                                {t('view_details')}
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem
                          className="gap-2 text-destructive focus:text-destructive"
                          onClick={() => setDeleteIds([row.id])}
                        >
                          <Trash2 size={14} aria-hidden />
                          {t('delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderBody = () => {
    if (isLoading) return <LoadingRows />;
    if (isError) {
      return (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      );
    }
    if (items.length === 0 && isFiltering) {
      return (
        <EmptyState
          icon={<Search size={28} />}
          title={t('no_results')}
          description={t('try_adjusting_your_search_or_filters')}
          secondaryAction={{ label: t('clear_filters'), onClick: clearFilters }}
        />
      );
    }
    if (items.length === 0) return <EmptyState icon={<Inbox size={28} />} title={t('no_results_to_show')} />;
    return renderRows();
  };

  return (
    <div className="flex flex-col gap-4">
      {isMobile ? (
        <div className="flex items-center justify-between gap-3">
          <h1 className="flex min-w-0 items-center gap-2 text-[17px] font-bold tracking-[-0.01em] text-foreground">
            {t('results')}
            {meta.total > 0 && (
              <span className="inline-flex h-5 items-center rounded-md bg-muted px-[7px] text-[11.5px] font-medium tabular-nums text-muted-foreground">
                {meta.total}
              </span>
            )}
          </h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-foreground/80"
                aria-label={t('export_results')}
                disabled={isExporting}
              >
                {isExporting ? <Loader2 className="h-[19px] w-[19px] animate-spin" /> : <Download className="h-[19px] w-[19px]" aria-hidden />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => handleExport('xlsx')} className="gap-2 cursor-pointer">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                <span>Excel (.xlsx)</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} className="gap-2 cursor-pointer">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span>CSV (.csv)</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <PageHeader
          title={
            <span className="flex items-center gap-2.5">
              {t('results_table')}
              {meta.total > 0 && (
                <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                  {meta.total}
                </span>
              )}
            </span>
          }
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" disabled={isExporting}>
                  {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {t('export_results')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => handleExport('xlsx')} className="gap-2 cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                  <span>Excel (.xlsx)</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')} className="gap-2 cursor-pointer">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>CSV (.csv)</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <TableSearch
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('results_search_placeholder')}
          aria-label={t('results_search_placeholder')}
        />
        <DateRangeFilter label={t('end_date')} value={endDate} onChange={setEndDate} />
        {hasFilters && <FilterReset onClick={clearFilters} />}
        {!isMobile && (
          <ColumnsToggle
            className="ml-auto h-9"
            columns={[
              { id: 'phone', label: t('phone'), visible: cols.phone },
              { id: 'email', label: t('email'), visible: cols.email },
              ...(resolvedSubjects.length > 0
                ? [{ id: 'subjects', label: t('subjects'), visible: cols.subjects }]
                : []),
              { id: 'score', label: t('overall_score'), visible: cols.score },
              { id: 'percentage', label: t('percentage'), visible: cols.percentage },
              { id: 'result', label: t('result'), visible: cols.result },
              { id: 'proctoringEvents', label: t('proctoring_events'), visible: cols.proctoringEvents },
              { id: 'endDate', label: t('end_date'), visible: cols.endDate },
              { id: 'time', label: t('time'), visible: cols.time },
            ]}
            onChange={(id, visible) => setCols((c) => ({ ...c, [id]: visible }))}
          />
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {/* While the next page loads the previous one stays, dimmed, so nothing jumps. */}
        <div className={cn('transition-opacity', isPlaceholderData && 'opacity-60')} aria-busy={isPlaceholderData || undefined}>
          {renderBody()}
        </div>
        {!isMobile && !isLoading && !isError && meta.total > 0 && (
          <div className="border-t border-border px-4 py-3">
            <TablePager
              page={page}
              pageCount={totalPages || 1}
              pageSize={perPage}
              total={meta.total}
              onPageChange={setPage}
              onPageSizeChange={setPerPage}
              noun={t('pager_noun_results')}
            />
          </div>
        )}
      </div>
      {isMobile && !isLoading && !isError && meta.total > 0 && (
        <CompactPager page={page} pageCount={totalPages || 1} pageSize={perPage} total={meta.total} onPageChange={setPage} />
      )}

      <ConfirmDialog
        open={deleteIds !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteIds(null);
        }}
        title={t('delete_selected')}
        description={t('confirm_delete_results_count', {
          count: deleteIds?.length ?? 0,
          defaultValue: 'This will permanently delete {{count}} result(s). This cannot be undone.',
        })}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
        loading={isDeleting}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ResultsTablePage;
